/**
 * Job report — photos by room, pairs by spot. Before on the left, after on the right, the items that live in
 * the spot as chips the owner or VA taps (Here → Missing → Broken). Tap a photo to open it full screen and step
 * through the room's pairs. Per the Owner Report - Photos by Spot design.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, ChevronLeft, ChevronRight, Clock, Loader2, Minus, Plus, RotateCw, X } from "lucide-react";
import { cn } from "@/lib/utils";

export type PairStatus = "here" | "missing" | "broken";

export type PairItem = {
  key: string;
  kind: "unit" | "supply" | "appliance";
  id: string;
  name: string;
  status: PairStatus;
  /** Marked by the owner or VA (or flagged by the cleaner). Unchecked items show no status. */
  checked: boolean;
};

export type PairSpot = {
  key: string;
  name: string;
  sub: string;
  beforeUrl: string | null;
  beforeTime: string;
  afterUrl: string | null;
  afterTime: string;
  items: PairItem[];
};

export type PairRoom = {
  id: string;
  name: string;
  skipText: string | null;
  spots: PairSpot[];
};

const NEXT: Record<"unchecked" | PairStatus, PairStatus> = {
  unchecked: "here",
  here: "missing",
  missing: "broken",
  broken: "here",
};

function chipClass(it: PairItem): string {
  if (!it.checked) return "border-[#d7e0e6] bg-white text-[#0f1720]";
  if (it.status === "missing") return "border-[#f2c9c3] bg-[#fdf1ef] text-[#b4453a]";
  if (it.status === "broken") return "border-[#f2ddc6] bg-[#fdf4e3] text-[#9a5a00]";
  return "border-[#bfe3dd] bg-[#f1f8f7] text-[#15776d]";
}

const statusWord = (s: PairStatus) => (s === "here" ? "Here" : s === "missing" ? "Missing" : "Broken");

function Photo({ url, label, time }: { url: string | null; label: string; time: string }) {
  return (
    <div className="relative aspect-[4/3] w-full overflow-hidden rounded-[14px] bg-[#e6edf1]">
      {url ? (
        <>
          <img src={url} alt="" loading="lazy" className="h-full w-full object-cover" />
          <span className="absolute left-2 top-2 rounded-full bg-[rgba(13,20,27,0.78)] px-2.5 py-1 text-[12px] font-bold text-white">
            {label}
            {time ? ` · ${time}` : ""}
          </span>
        </>
      ) : (
        <div className="flex h-full w-full flex-col items-center justify-center gap-1.5 rounded-[14px] border-2 border-dashed border-[#cfdbe2] bg-[#f4f7f9] text-[13px] font-bold text-[#8494a2]">
          <Clock className="h-4 w-4" />
          Not taken yet
        </div>
      )}
    </div>
  );
}

const ROT_KEY = "stravo-photo-rotation";
const readRot = (url: string): number => {
  try {
    return Number((JSON.parse(localStorage.getItem(ROT_KEY) || "{}") as Record<string, number>)[url] ?? 0) || 0;
  } catch {
    return 0;
  }
};
const saveRot = (url: string, deg: number) => {
  try {
    const all = JSON.parse(localStorage.getItem(ROT_KEY) || "{}") as Record<string, number>;
    all[url] = deg;
    localStorage.setItem(ROT_KEY, JSON.stringify(all));
  } catch {
    /* rotation just won't be remembered */
  }
};

/** One photo: rotate in quarter turns (remembered per photo), zoom with buttons, wheel or double-click, drag to move. */
function ZoomPanel({ url, label, time }: { url: string; label: string; time: string }) {
  const boxRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ w: 1, h: 1 });
  const [nat, setNat] = useState({ w: 1, h: 1 });
  const [rot, setRot] = useState(() => readRot(url));
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const drag = useRef<{ x: number; y: number; px: number; py: number } | null>(null);

  useEffect(() => {
    setRot(readRot(url));
    setScale(1);
    setPos({ x: 0, y: 0 });
  }, [url]);
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setBox({ w: el.clientWidth || 1, h: el.clientHeight || 1 }));
    ro.observe(el);
    setBox({ w: el.clientWidth || 1, h: el.clientHeight || 1 });
    return () => ro.disconnect();
  }, []);

  const turned = rot % 180 !== 0;
  const fit = Math.min(box.w / (turned ? nat.h : nat.w), box.h / (turned ? nat.w : nat.h));
  const zoom = (f: number) =>
    setScale((v) => {
      const next = Math.min(6, Math.max(1, v * f));
      if (next === 1) setPos({ x: 0, y: 0 });
      return next;
    });
  const rotate = () =>
    setRot((r) => {
      const next = (r + 90) % 360;
      saveRot(url, next);
      return next;
    });
  const btn = "flex h-10 w-10 items-center justify-center rounded-full bg-[rgba(13,20,27,0.78)] text-white hover:bg-[rgba(13,20,27,0.95)]";

  return (
    <div
      ref={boxRef}
      className="relative min-h-0 flex-1 touch-none select-none overflow-hidden rounded-[16px] bg-black/40"
      onWheel={(e) => zoom(e.deltaY < 0 ? 1.15 : 1 / 1.15)}
      onDoubleClick={() => (scale > 1 ? (setScale(1), setPos({ x: 0, y: 0 })) : setScale(2.5))}
      onPointerDown={(e) => {
        if (scale <= 1) return;
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
        drag.current = { x: e.clientX, y: e.clientY, px: pos.x, py: pos.y };
      }}
      onPointerMove={(e) => {
        const d = drag.current;
        if (d) setPos({ x: d.px + e.clientX - d.x, y: d.py + e.clientY - d.y });
      }}
      onPointerUp={() => (drag.current = null)}
      style={{ cursor: scale > 1 ? "grab" : "zoom-in" }}
    >
      <img
        src={url}
        alt=""
        draggable={false}
        onLoad={(e) => setNat({ w: e.currentTarget.naturalWidth || 1, h: e.currentTarget.naturalHeight || 1 })}
        className="pointer-events-none absolute left-1/2 top-1/2 max-w-none"
        style={{
          width: nat.w,
          height: nat.h,
          transform: `translate(-50%, -50%) translate(${pos.x}px, ${pos.y}px) rotate(${rot}deg) scale(${fit * scale})`,
          transition: drag.current ? "none" : "transform 160ms ease-out",
        }}
      />
      <span className="absolute left-3 top-3 rounded-full bg-[rgba(13,20,27,0.78)] px-3 py-1 text-[12.5px] font-bold text-white">
        {label}
        {time ? ` · ${time}` : ""}
      </span>
      <div className="absolute right-3 top-3 flex flex-col gap-2">
        <button type="button" onClick={rotate} aria-label="Rotate" className={btn}>
          <RotateCw className="h-[18px] w-[18px]" />
        </button>
        <button type="button" onClick={() => zoom(1.4)} aria-label="Zoom in" className={btn}>
          <Plus className="h-[18px] w-[18px]" />
        </button>
        <button type="button" onClick={() => zoom(1 / 1.4)} aria-label="Zoom out" className={btn}>
          <Minus className="h-[18px] w-[18px]" />
        </button>
      </div>
    </div>
  );
}

function PairLightbox({
  pairs,
  index,
  onIndex,
  onClose,
}: {
  pairs: { title: string; sub: string; beforeUrl: string | null; beforeTime: string; afterUrl: string | null; afterTime: string }[];
  index: number;
  onIndex: (i: number) => void;
  onClose: () => void;
}) {
  const p = pairs[index];
  if (!p) return null;
  return (
    <div className="fixed inset-0 z-[90] flex flex-col bg-[#0d141b]">
      <div className="flex items-center justify-between gap-3 px-5 pb-3 pt-5 text-white">
        <div className="min-w-0">
          <div className="truncate text-[18px] font-bold">{p.title}</div>
          <div className="text-[13px] font-medium text-white/70">
            {p.sub} · {index + 1} of {pairs.length}
          </div>
        </div>
        <button type="button" onClick={onClose} aria-label="Close" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/15">
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="mx-auto flex min-h-0 w-full max-w-[1500px] flex-1 flex-col gap-3 px-5 pb-3 md:flex-row">
        {[
          { label: "Before", url: p.beforeUrl, time: p.beforeTime },
          { label: "After", url: p.afterUrl, time: p.afterTime },
        ].map((x) =>
          x.url ? (
            <ZoomPanel key={`${x.label}-${x.url}`} url={x.url} label={x.label} time={x.time} />
          ) : (
            <div key={x.label} className="flex min-h-0 flex-1 items-center justify-center rounded-[16px] border-2 border-dashed border-white/30 text-[15px] font-bold text-white/70">
              {x.label}: Not taken yet
            </div>
          ),
        )}
      </div>
      <div className="flex items-center justify-center gap-3 pb-6">
        <button
          type="button"
          disabled={index === 0}
          onClick={() => onIndex(index - 1)}
          className="flex h-12 w-12 items-center justify-center rounded-full bg-white/15 text-white disabled:opacity-30"
          aria-label="Previous"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
        <button
          type="button"
          disabled={index === pairs.length - 1}
          onClick={() => onIndex(index + 1)}
          className="flex h-12 w-12 items-center justify-center rounded-full bg-white/15 text-white disabled:opacity-30"
          aria-label="Next"
        >
          <ChevronRight className="h-6 w-6" />
        </button>
      </div>
    </div>
  );
}

export function ReportPairs({
  rooms,
  inProgress,
  busyKey,
  onCycle,
  onAllHere,
}: {
  rooms: PairRoom[];
  inProgress: boolean;
  busyKey: string | null;
  /** Tap a chip: unchecked → Here → Missing → Broken → Here. */
  onCycle: (item: PairItem, next: PairStatus) => void;
  onAllHere: (items: PairItem[]) => void;
}) {
  const [open, setOpen] = useState<Record<string, boolean>>(() => {
    const first = rooms.find((r) => r.spots.length > 0);
    return first ? { [first.id]: true } : {};
  });
  const [lb, setLb] = useState<{ roomId: string; index: number } | null>(null);

  const lbPairs = useMemo(() => {
    const room = rooms.find((r) => r.id === lb?.roomId);
    return (room?.spots ?? []).map((s) => ({
      title: s.name,
      sub: room?.name ?? "",
      beforeUrl: s.beforeUrl,
      beforeTime: s.beforeTime,
      afterUrl: s.afterUrl,
      afterTime: s.afterTime,
    }));
  }, [rooms, lb?.roomId]);

  return (
    <div className="flex flex-col gap-2.5">
      {rooms.map((room) => {
        const items = room.spots.flatMap((s) => s.items);
        const checked = items.filter((i) => i.checked).length;
        const missing = items.filter((i) => i.checked && i.status === "missing").length;
        const broken = items.filter((i) => i.checked && i.status === "broken").length;
        const afterIn = room.spots.filter((s) => s.afterUrl).length;
        const isOpen = !!open[room.id];
        return (
          <div key={room.id} className="rounded-[20px] bg-[#f4f7f9]">
            <button
              type="button"
              onClick={() => setOpen((o) => ({ ...o, [room.id]: !o[room.id] }))}
              className="flex w-full flex-wrap items-center gap-x-3 gap-y-1.5 px-4 py-3.5 text-left"
            >
              <ChevronDown className={cn("h-4 w-4 shrink-0 text-[#64748b] transition-transform", !isOpen && "-rotate-90")} />
              <span className="text-[18px] font-bold tracking-[-0.2px] text-[#0f1720]">{room.name}</span>
              <span className="text-[13px] font-medium text-[#8494a2]">
                {room.skipText
                  ? room.skipText
                  : inProgress
                    ? `${afterIn} of ${room.spots.length} after photos in`
                    : `${room.spots.length} pair${room.spots.length === 1 ? "" : "s"} · ${items.length} item${items.length === 1 ? "" : "s"}`}
              </span>
              <span className="ml-auto flex flex-wrap items-center gap-2">
                {missing > 0 ? <span className="rounded-full bg-[#fdf1ef] px-2.5 py-1 text-[12.5px] font-bold text-[#b4453a]">{missing} missing</span> : null}
                {broken > 0 ? <span className="rounded-full bg-[#fdf4e3] px-2.5 py-1 text-[12.5px] font-bold text-[#9a5a00]">{broken} broken</span> : null}
                {items.length > 0 ? (
                  <span className="rounded-full bg-[#e2eeed] px-2.5 py-1 text-[12.5px] font-bold text-[#15776d]">
                    {checked} of {items.length} checked
                  </span>
                ) : null}
              </span>
            </button>
            {isOpen && room.spots.length > 0 ? (
              <div className="grid gap-3 px-3 pb-3 lg:grid-cols-2">
                {room.spots.map((spot, si) => {
                  const allDone = spot.items.length > 0 && spot.items.every((i) => i.checked && i.status === "here");
                  return (
                    <div key={spot.key} className="flex flex-col gap-3 rounded-[18px] bg-white p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-[16.5px] font-bold text-[#0f1720]">{spot.name}</div>
                          <div className="text-[13px] font-medium text-[#8494a2]">{spot.sub}</div>
                        </div>
                        {spot.items.length > 0 ? (
                          <button
                            type="button"
                            onClick={() => onAllHere(spot.items.filter((i) => !i.checked))}
                            disabled={allDone}
                            className={cn(
                              "flex h-10 shrink-0 items-center gap-1.5 rounded-[12px] px-3.5 text-[13.5px] font-bold",
                              allDone ? "bg-[#e2eeed] text-[#15776d]" : "bg-[#15776d] text-white",
                            )}
                          >
                            <Check className="h-4 w-4" strokeWidth={3} />
                            Everything here
                          </button>
                        ) : null}
                      </div>
                      <div className="grid grid-cols-2 gap-2.5">
                        <button
                          type="button"
                          disabled={!spot.beforeUrl}
                          onClick={() => setLb({ roomId: room.id, index: si })}
                          className="text-left"
                        >
                          <Photo url={spot.beforeUrl} label="Before" time={spot.beforeTime} />
                        </button>
                        <button
                          type="button"
                          disabled={!spot.afterUrl && !spot.beforeUrl}
                          onClick={() => setLb({ roomId: room.id, index: si })}
                          className="text-left"
                        >
                          <Photo url={spot.afterUrl} label="After" time={spot.afterTime} />
                        </button>
                      </div>
                      {spot.items.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {spot.items.map((it) => (
                            <button
                              key={it.key}
                              type="button"
                              disabled={busyKey === it.key}
                              onClick={() => onCycle(it, NEXT[it.checked ? it.status : "unchecked"])}
                              className={cn(
                                "flex min-h-[40px] items-center gap-2 rounded-full border-[1.5px] px-3.5 text-[14px] font-bold",
                                chipClass(it),
                              )}
                            >
                              {it.name}
                              {busyKey === it.key ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : it.checked ? (
                                <span className="rounded-full bg-white/80 px-2 py-0.5 text-[11.5px] font-bold">{statusWord(it.status)}</span>
                              ) : null}
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>
        );
      })}
      {lb ? <PairLightbox pairs={lbPairs} index={lb.index} onIndex={(i) => setLb({ roomId: lb.roomId, index: i })} onClose={() => setLb(null)} /> : null}
    </div>
  );
}
